import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import patientServices from '../../core/services/patientServices';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const COUNTRY_CODES = [
  { code: '+91', name: 'India', length: 10 },
  { code: '+1', name: 'USA/Canada', length: 10 },
  { code: '+44', name: 'UK', length: 10 },
  { code: '+971', name: 'UAE', length: 9 },
  { code: '+61', name: 'Australia', length: 9 },
  { code: '+65', name: 'Singapore', length: 8 },
];

const PATIENT_INITIAL = {
  fname: '', mname: '', lname: '',
  phno: '', email: '', dob: '', gender: '', age: '',
  bloodgroup: '', address: '', area: '', city: 'Mangalore', state: 'Karnataka', pincode: '',
  adhar: '', occupation: '', education: '', language: '', sociostatus: '',
  altPhno: '', emrPhono: '', fathern: '', mothern: '', fathernorspouse: '',
  srcRef: '', custGroupId: '', erpCustId: '', fileNo: '', ehrid: '', comments: '',
  status: 'ACTIVE', imgurl: null, countryCode: '+91',
  patientType: '', custGrpDesc: ''
};

const toNull = (val) => {
  if (val === '' || val === undefined || val === null) return null;
  return typeof val === 'string' ? val.trim() || null : val;
};

const toNumberOrNull = (val) => {
  if (val === '' || val === undefined || val === null) return null;
  const number = Number(val);
  return Number.isNaN(number) ? null : number;
};

const maskAadhar = (val) => {
  const digits = (val || '').replace(/\D/g, '');
  return digits ? `**** **** ${digits.slice(-4)}` : null;
};

const encryptAadhar = (val) => {
  const digits = (val || '').replace(/\D/g, '');
  return digits ? btoa(digits) : null;
};

const maskPhone = (val) => {
  const digits = (val || '').replace(/\D/g, '');
  return digits ? `******${digits.slice(-4)}` : null;
};

// Auto-generate age from date of birth
const calculateAge = (dobString) => {
  if (!dobString) return '';
  const dob = new Date(dobString);
  if (Number.isNaN(dob.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : '';
};

export default function PatientFormModal({ isOpen, onClose, onPatientCreated }) {
  const [form, setForm] = useState(PATIENT_INITIAL);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const ageManuallyEditedRef = useRef(false);

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  // ===== CHANGE 1: Auto-capitalize handler for name fields =====
  // Converts value to uppercase in JS before it hits state, so both the
  // displayed text AND the submitted payload value are uppercase.
  const setUppercase = (key, value) => {
    set(key, value.toUpperCase());
  };
  // ===============================================================

  const handleDobChange = (value) => {
    setForm(prev => ({
      ...prev,
      dob: value,
      age: ageManuallyEditedRef.current ? prev.age : calculateAge(value),
    }));
    setErrors(prev => ({ ...prev, dob: '', age: '' }));
  };

  const handlePhoneChange = (key, rawValue) => {
    const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 10);
    set(key, digitsOnly);
  };

  const validate = () => {
    const newErrors = {};
    if (!form.fname?.trim()) newErrors.fname = 'First name is required';
    if (!form.phno?.trim()) newErrors.phno = 'Phone number is required';
    else if (form.phno.length !== 10) newErrors.phno = 'Phone number must be exactly 10 digits';
    if (form.altPhno && form.altPhno.length !== 10) newErrors.altPhno = 'Alternate phone must be exactly 10 digits';
    if (!form.gender || !form.gender.trim()) newErrors.gender = 'Gender is required';
    if (!form.age) newErrors.age = 'Age is required';
    if (!form.city?.trim()) newErrors.city = 'City is required';
    if (!form.state?.trim()) newErrors.state = 'State is required'; 

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await patientServices.uploadPatientImage(file);
      set('imgurl', data?.data?.url || data?.url || data);
      toast.success('Image uploaded');
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const fullName = [form.fname, form.mname, form.lname].map(toNull).filter(Boolean).join(' ');
      const payload = {
        fname: toNull(form.fname),
        mname: toNull(form.mname),
        lname: toNull(form.lname),
        phno: toNull(form.phno),
        email: toNull(form.email),
        dob: toNull(form.dob),
        gender: toNull(form.gender),
        age: toNumberOrNull(form.age),
        bloodgroup: toNull(form.bloodgroup),
        address: toNull(form.address),
        area: toNull(form.area),
        city: toNull(form.city),
        state: toNull(form.state),
        pincode: toNull(form.pincode),
        adhar: toNull(form.adhar),
        occupation: toNull(form.occupation),
        education: toNull(form.education),
        language: toNull(form.language),
        sociostatus: toNull(form.sociostatus),
        altPhno: toNull(form.altPhno),
        emrPhono: toNull(form.emrPhono),
        fathern: toNull(form.fathern),
        mothern: toNull(form.mothern),
        fathernorspouse: toNull(form.fathernorspouse),
        srcRef: toNull(form.srcRef),
        custGroupId: toNull(form.custGroupId),
        erpCustId: toNumberOrNull(form.erpCustId),
        fileNo: toNull(form.fileNo),
        ehrid: toNull(form.ehrid),
        comments: toNull(form.comments),
        status: 'ACTIVE',
        imgurl: form.imgurl,
        encryptAdhar: encryptAadhar(form.adhar),
        maskAdhar: maskAadhar(form.adhar),
        maskName: fullName ? `${fullName[0]}***` : null,
        maskPhno: maskPhone(form.phno),
        ageUpdated: false
      };

      const response = await patientServices.addPatient(payload);
      toast.success('Patient created successfully!');
      onPatientCreated(response.patientresp);
      setForm(PATIENT_INITIAL);
      ageManuallyEditedRef.current = false;
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create patient');
    } finally {
      setSaving(false);
    }
  };

  // ===== CHANGE 1: shared sx for uppercase name fields =====
  // MUI equivalent of `text-transform: uppercase` in CSS, applied to the
  // actual rendered <input> inside the TextField.
  const uppercaseSx = { '& input': { textTransform: 'uppercase' } };
  // ===========================================================

  // ==============================================================

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Patient Registration</Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive patient documentation and demographic profile
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: 'hidden'}}> 
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>

            {/* ===== CHANGE 1: First Name - uppercase via JS + sx ===== */}
            <Grid item xs={12} md={4}>
              <TextField
                label="First Name *"
                fullWidth
                size="small"
                value={form.fname}
                onChange={e => setUppercase('fname', e.target.value)}
                placeholder="Enter first name"
                error={!!errors.fname}
                helperText={errors.fname}
                sx={uppercaseSx}
              />
            </Grid>
            {/* ========================================================= */}

            {/* ===== CHANGE 1: Middle Name - uppercase via JS + sx ===== */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Middle Name"
                fullWidth
                size="small"
                value={form.mname}
                onChange={e => setUppercase('mname', e.target.value)}
                placeholder="Middle name"
                sx={uppercaseSx}
              />
            </Grid>
            {/* ========================================================== */}

            {/* ===== CHANGE 1: Last Name - uppercase via JS + sx ===== */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Last Name"
                fullWidth
                size="small"
                value={form.lname}
                onChange={e => setUppercase('lname', e.target.value)}
                placeholder="Enter last name"
                error={!!errors.lname}
                helperText={errors.lname}
                sx={uppercaseSx}
              />
            </Grid>
            {/* ======================================================== */}

            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Gender *"
                fullWidth
                size="small"
                value={form.gender}
                onChange={e => set('gender', e.target.value)}
                error={!!errors.gender}
                helperText={errors.gender}
              >
                <MenuItem value=""><em>Select Gender</em></MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Date of Birth"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.dob}
                onChange={e => handleDobChange(e.target.value)}
                error={!!errors.dob}
                helperText={errors.dob}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                label="Age *"
                fullWidth
                size="small"
                value={form.age}
                onChange={e => { ageManuallyEditedRef.current = true; set('age', e.target.value); }}
                placeholder="Auto-filled from DOB or enter manually"
                error={!!errors.age}
                helperText={errors.age}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Primary Phone *"
                fullWidth
                size="small"
                value={form.phno}
                onChange={e => handlePhoneChange('phno', e.target.value)}
                placeholder="10 digit phone number"
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                error={!!errors.phno}
                helperText={errors.phno}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TextField
                        select
                        variant="standard"
                        value={form.countryCode}
                        onChange={e => set('countryCode', e.target.value)}
                        sx={{ minWidth: 70 }}
                        SelectProps={{ disableUnderline: true }}
                      >
                        {COUNTRY_CODES.map(c => (
                          <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>
                        ))}
                      </TextField>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Aadhar No"
                fullWidth
                size="small"
                value={form.adhar}
                onChange={e => set('adhar', e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="12 digit Aadhar"
                inputProps={{ maxLength: 12 }}
              />
            </Grid>

            {/* ===== CHANGE 2: City - locked with disabled + lockedSx ===== */}
            <Grid item xs={12} md={4}>
              <TextField
                label="City *"
                fullWidth
                size="small"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder="Enter city"
                error={!!errors.city}
                helperText={errors.city}
              />
            </Grid>
            {/* ================================================================ */}

            {/* ===== CHANGE 2: State - locked with disabled + lockedSx ===== */}
            <Grid item xs={12} md={4}>
              <TextField
                label="State *"   
                fullWidth
                size="small"
                value={form.state}
                onChange={(e) => set('state', e.target.value)}
                placeholder="Enter state"
                error={!!errors.state}
                helperText={errors.state}   
              />
            </Grid>  
            {/* ================================================================= */}

          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={saving} variant="contained">
          {saving ? 'Creating...' : 'Create Patient'}
        </Button>
      </DialogActions> 
    </Dialog>
  );
}